import * as React from 'react'
import { Link } from 'react-router-dom'
import { useIntl } from 'react-intl'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import { TableHead } from '@mui/material'
import ArrowRightIcon from '../../../../assets/icons/Arrows-chevron/Arrow-Right.svg'
import ConfirmDelete from '../../../../components/dialogs/ConfirmDelete'
import ConfirmDuplicate from '../../../../components/dialogs/ConfirmDuplicate'
import EditIcon from '../../../../assets/icons/Edit/Edit.svg'
import TablePaginationActions from '../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import { CourseState } from '../../../../features/types'

interface Props {
    coursesData: CourseState[]
    onDelete: (id: number) => void
    onDuplicate: (id: number) => void
}

const CoursesTable: React.FC<Props> = ({ coursesData = [], onDelete, onDuplicate }) => {
    const { formatMessage } = useIntl()
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(5)

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0
            ? Math.max(0, (1 + page) * rowsPerPage - coursesData.length)
            : 0

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    return (
        <TableContainer
            component={Paper}
            sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
            elevation={0}
        >
            <Table
                sx={{ minWidth: 500 }}
                aria-label="custom pagination table"
            >
                <TableHead sx={{ bgcolor: 'grey.100' }}>
                    <TableRow>
                        <TableCell>
                            {formatMessage({ id: 'titleKz' })}
                        </TableCell>

                        <TableCell>
                            {formatMessage({ id: 'titleRu' })}
                        </TableCell>

                        <TableCell align="right">
                            {formatMessage({ id: 'subjects' })}
                        </TableCell>

                        <TableCell align="right">
                            {formatMessage({ id: 'edit' })}
                        </TableCell>

                        <TableCell align="right">
                            {formatMessage({ id: 'delete' })}
                        </TableCell>

                        <TableCell align="right">
                            {formatMessage({ id: 'duplicate' })}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {(rowsPerPage > 0
                        ? coursesData.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                        )
                        : coursesData
                    ).map((row) => (
                        <TableRow key={row?.id}>
                            <TableCell
                                component="th"
                                scope="row"
                                sx={{ width: '320px' }}
                            >
                                {row?.titleKz}
                            </TableCell>

                            <TableCell
                                component="th"
                                scope="row"
                            >
                                {row?.titleRu}
                            </TableCell>

                            <TableCell
                                style={{ width: 60 }}
                                align="right"
                            >
                                <Link
                                    to={`${row?.id}`}
                                    state={{
                                        course: row,
                                    }}
                                >
                                    <IconButton sx={{ background: '#f3f4f5' }}>
                                        <img
                                            src={ArrowRightIcon}
                                            alt="details"
                                        />
                                    </IconButton>
                                </Link>
                            </TableCell>

                            <TableCell
                                style={{ width: 60 }}
                                align="right"
                            >
                                <Link to={`edit/${row?.id}`}>
                                    <IconButton sx={{ background: '#f3f4f5' }}>
                                        <img
                                            src={EditIcon}
                                            alt="edit"
                                        />
                                    </IconButton>
                                </Link>
                            </TableCell>

                            <TableCell
                                style={{ width: 60 }}
                                align="right"
                            >
                                <ConfirmDelete
                                    confirmAction={() => onDelete(row?.id)}
                                />
                            </TableCell>

                            <TableCell
                                style={{ width: 60 }}
                                align="right"
                            >
                                <ConfirmDuplicate
                                    confirmAction={() => onDuplicate(row?.id)}
                                />
                            </TableCell>
                        </TableRow>
                    ))}

                    {emptyRows > 0 && (
                        <TableRow style={{ height: 53 * emptyRows }}>
                            <TableCell colSpan={6} />
                        </TableRow>
                    )}
                </TableBody>

                <TableFooter>
                    <TableRow>
                        <TablePagination
                            rowsPerPageOptions={[
                                5,
                                10,
                                25,
                                { label: 'All', value: -1 },
                            ]}
                            colSpan={6}
                            count={coursesData?.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            SelectProps={{
                                inputProps: {
                                    'aria-label': 'rows per page',
                                },
                                native: true,
                            }}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            sx={{ border: 0 }}
                        />
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
    )
}

export default CoursesTable
